import { redirect } from "react-router";
import useUserState from "~/stores/user";

// If the prefix is undefined it will default to "Frontend.SO." in the isPermitted function
export function pageGuard(key: SoPermission, prefix?: string) {
  const { isPermitted, isLoggedIn } = useUserState.getState();
  if (!isLoggedIn()) return redirect("/login");
  if (!isPermitted(key, prefix)) return redirect("/");

  return {};
}

// If the prefix is undefined it will default to "Frontend.SO." in the isPermitted function
export function permitted(key: SoPermission, prefix?: string): boolean {
  const { isPermitted, isLoggedIn } = useUserState.getState();
  return isLoggedIn() && isPermitted(key, prefix);
}

export type JwtResult = {
  exp: number;
  iat: number;
};

export function parseJwt(token: string): JwtResult {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload) as JwtResult;
}

export type SoPermission =
  | "Frontend.IMD.Report.Delete"
  | "Mqtt.Create"
  | "Script.Delete"
  | "Mqtt.Update"
  | "Gateways.Update"
  | "GlobalSettings.Update"
  | "Company.Update"
  | "Dashboard.Update"
  | "Frontend.IMD.ReportHistory.View"
  | "Roles.GetCurrentUserPermissionsFrontend"
  | "GlobalSettings.View"
  | "Frontend.IMD.Facility.View"
  | "Frontend.IMD.Tariff.View"
  | "Frontend.IMD.ReportHistory.Download"
  | "Report.Delete"
  | "Modbus.Create"
  | "Alarm.View"
  | "Application.Create"
  | "Script.View"
  | "TagPool.Create"
  | "Users.GetDefaultRoleKey"
  | "Gateways.Create"
  | "Frontend.IMD.Device.Delete"
  | "SuperAdmin"
  | "TagPool.Update"
  | "SignalTest.Create"
  | "Actions.Delete"
  | "Users.GetUserCompany"
  | "Report.Create"
  | "Company.Delete"
  | "User.Delete"
  | "Opc.View"
  | "Gateways.View"
  | "Application.Delete"
  | "Tag.Update"
  | "Frontend.IMD.Facility.Create"
  | "User.Create"
  | "PublicDashboard.Delete"
  | "Frontend.IMD.Device.Create"
  | "Opc.Update"
  | "Formula.Delete"
  | "UserRole.Update"
  | "Alarm.Update"
  | "Mqtt.View"
  | "Frontend.IMD.Spot.Delete"
  | "Opc.Delete"
  | "Frontend.IMD.Report.View"
  | "PublicDashboard.Create"
  | "Users.Get"
  | "Modbus.Update"
  | "Frontend.IMD.Location.Delete"
  | "Opc.Create"
  | "Frontend.IMD.Tariff.Delete"
  | "DashboardModule.Delete"
  | "Users.GetClaims"
  | "Application.Update"
  | "Notification.DeactivateNotification"
  | "Frontend.IMD.Device.Update"
  | "TagPool.Delete"
  | "Company.Create"
  | "Frontend.IMD.Tariff.Create"
  | "DashboardModule.Update"
  | "Users.GetOneTimeToken"
  | "Roles.GetCurrentUserPermissions"
  | "Company.View"
  | "SignalTest.Update"
  | "Report.View"
  | "Script.Create"
  | "Actions.View"
  | "User.Update"
  | "Frontend.IMD.Report.Update"
  | "Formula.Create"
  | "Script.Update"
  | "TagPool.View"
  | "Report.Update"
  | "Frontend.IMD.Location.Update"
  | "Dashboard.Create"
  | "Tag.View"
  | "Frontend.IMD.Spot.Update"
  | "Actions.Create"
  | "Roles.Delete"
  | "Application.View"
  | "PublicDashboard.View"
  | "SignalTest.Delete"
  | "Frontend.IMD.Home.View"
  | "Frontend.IMD.Location.Create"
  | "Roles.Create"
  | "Gateways.Delete"
  | "Mqtt.Delete"
  | "Settings.GetSettings"
  | "Dashboard.View"
  | "Modbus.Delete"
  | "Frontend.IMD.Location.View"
  | "Tag.Delete"
  | "Roles.Update"
  | "SignalTest.View"
  | "Settings.SetSettings"
  | "Settings.View"
  | "Frontend.IMD.Facility.Delete"
  | "Frontend.IMD.Tariff.Update"
  | "Modbus.View"
  | "Frontend.IMD.NavigationTree.View"
  | "Frontend.IMD.Facility.Update"
  | "Roles.View"
  | "Alarm.Delete"
  | "DashboardModule.Create"
  | "Frontend.IMD.Spot.View"
  | "Notification.GetNotifications"
  | "Tag.Create"
  | "Actions.Update"
  | "Dashboard.Delete"
  | "Formula.View"
  | "Frontend.IMD.Spot.Create"
  | "Alarm.Create"
  | "Settings.Update"
  | "Frontend.IMD.Report.Create"
  | "CompanySettings.Update"
  | "CompanySettings.View"
  | "Settings.Create"
  | "Frontend.IMD.Device.View"
  | "Notification.DeactivateAllNotifications"
  | "Formula.Update";
